/** Debug overlay: computed font size per text carrier on resume preview. */

import { classifyResumeLineGuideBlock, visualOffsetYToLocalGuideY } from './resumePreviewLineGuides'

const FONT_SIZE_CARRIER_SELECTORS = [
  'h1',
  'h2',
  'h3',
  'p',
  '.contact-detail-line',
  'li > span.flex-1',
  'li > .paysafe-preview-bullet-text',
  '.paysafe-skill-block h3',
  '.paysafe-skill-block .paysafe-skill-body',
  '.paysafe-skill-block .paysafe-skill-token',
  '.paysafe-right-block > div',
  '.paysafe-right-block > a',
  '.paysafe-right-block > h3',
].join(',')

const LABEL_HEIGHT_PX = 13
const LABEL_GAP_PX = 2
/** Width reserved in the left margin; labels never sit on top of text lines. */
export const FONT_SIZE_LABEL_GUTTER_PX = 54

export type ResumeFontSizeLabel = {
  id: string
  block: string
  labelTop: number
  labelLeft: number
  anchorTop: number
  fontSizePx: number
  fontSizePt: number
  lineHeightPx: number
  fontWeight: number
  preview: string
}

/** CSS px (96dpi) → typographic pt (72dpi). */
export function cssPxToPt(px: number): number {
  if (!Number.isFinite(px) || px <= 0) return 0
  return Math.round(px * 0.75 * 10) / 10
}

function parseFontWeight(style: CSSStyleDeclaration): number {
  const w = style.fontWeight
  if (w === 'bold') return 700
  if (w === 'normal') return 400
  const n = parseInt(w, 10)
  return Number.isFinite(n) ? n : 400
}

function shouldSkipFontSizeCarrier(el: HTMLElement): boolean {
  const text = el.textContent?.trim()
  if (!text) return true
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return true
  if (el.classList.contains('border-b') || el.classList.contains('border-t')) return true
  const parent = el.parentElement
  if (parent?.tagName === 'LI' && el.tagName === 'SPAN' && !el.classList.contains('paysafe-preview-bullet-text')) {
    return true
  }
  if (el.tagName === 'LI' && el.querySelector('li > .paysafe-preview-bullet-text, li > span.flex-1')) {
    return true
  }
  return false
}

type RawFontLabel = Omit<ResumeFontSizeLabel, 'labelTop' | 'labelLeft'>

/**
 * Place badges in the left gutter (negative X). Vertical stacking stays in the gutter only —
 * never pushes resume lines.
 */
export function layoutFontSizeLabelsInLeftGutter(
  raw: RawFontLabel[],
  gutterWidthPx = FONT_SIZE_LABEL_GUTTER_PX
): ResumeFontSizeLabel[] {
  const sorted = [...raw].sort((a, b) => a.anchorTop - b.anchorTop)
  let gutterStackBottom = -Infinity
  const out: ResumeFontSizeLabel[] = []

  for (const item of sorted) {
    let labelTop = item.anchorTop
    if (labelTop < gutterStackBottom + LABEL_GAP_PX) {
      labelTop = gutterStackBottom + LABEL_GAP_PX
    }
    gutterStackBottom = labelTop + LABEL_HEIGHT_PX
    out.push({
      ...item,
      labelTop,
      labelLeft: -gutterWidthPx,
    })
  }

  return out
}

/** @deprecated use layoutFontSizeLabelsInLeftGutter — kept for tests */
export function layoutFontSizeLabelsWithoutOverlap(
  raw: RawFontLabel[],
  minLeftPx = 0
): ResumeFontSizeLabel[] {
  return layoutFontSizeLabelsInLeftGutter(raw).map((item) => ({
    ...item,
    labelLeft: Math.max(minLeftPx, item.labelLeft),
  }))
}

export type CollectResumePreviewFontSizeLabelsOptions = {
  scopeSelector?: string
  coordinateScale?: number
}

export function collectResumePreviewFontSizeLabels(
  root: HTMLElement,
  options: CollectResumePreviewFontSizeLabelsOptions = {}
): ResumeFontSizeLabel[] {
  const coordinateScale = options.coordinateScale ?? 1
  const scopeRoot =
    options.scopeSelector != null
      ? root.querySelector<HTMLElement>(options.scopeSelector) ?? null
      : root
  if (!scopeRoot) return []

  const rootRect = root.getBoundingClientRect()
  const rootTop = rootRect.top
  const raw: RawFontLabel[] = []
  const seen = new Set<HTMLElement>()

  scopeRoot.querySelectorAll(FONT_SIZE_CARRIER_SELECTORS).forEach((node, index) => {
    if (!(node instanceof HTMLElement)) return
    if (seen.has(node)) return
    if (shouldSkipFontSizeCarrier(node)) return
    seen.add(node)

    const rect = node.getBoundingClientRect()
    if (rect.width < 1 || rect.height < 1) return

    const style = window.getComputedStyle(node)
    const fontSizePx = parseFloat(style.fontSize) || 0
    if (fontSizePx <= 0) return

    const anchorTop = visualOffsetYToLocalGuideY(rect.top - rootTop, coordinateScale)
    const block = classifyResumeLineGuideBlock(node)
    const preview = (node.textContent?.trim() ?? '').replace(/\s+/g, ' ').slice(0, 28)

    raw.push({
      id: `${block}-${index}-${Math.round(anchorTop)}`,
      block,
      anchorTop,
      fontSizePx: Math.round(fontSizePx * 10) / 10,
      fontSizePt: cssPxToPt(fontSizePx),
      lineHeightPx: Math.round((parseFloat(style.lineHeight) || fontSizePx * 1.17) * 10) / 10,
      fontWeight: parseFontWeight(style),
      preview,
    })
  })

  return layoutFontSizeLabelsInLeftGutter(raw)
}

/** Unique pt sizes on the page, sorted descending. */
export function uniqueFontSizePtOnPage(labels: ResumeFontSizeLabel[]): number[] {
  const set = new Set<number>()
  for (const l of labels) set.add(l.fontSizePt)
  return Array.from(set).sort((a, b) => b - a)
}
